import { useState } from 'react';
import { useDealershipCityByDn } from '../../../hooks/useDealershipCityByDn';
import type { Occurrence, OccurrenceWithTimeSpent } from '../../../models/occurrence';
import { GenericCard } from '../../kanban/components/genericCards';
import { UptimeDrawer } from '../../shared';
import {
    findStep,
    getOccurrenceTotalMs,
    getOccurrenceTypeLabelId,
    getStepEstimateMs,
    getStepLabelId,
    isNextStampOccurrence,
    toStepValue,
} from '../trackingUtils';
import type { StepCardProps, SummaryProps } from '../types';
import CriticalityBadge from './CriticalityBadge';
import DrawerStepRow from './DrawerStepRow';
import OccurrenceSummaryDialog from './OccurrenceSummaryDialog';
import useMtText from './useMtText';
import './OccurrenceDrawer.scss';

interface IOccurrenceDrawerProps {
    occurrence: Occurrence;
    timeSpent?: OccurrenceWithTimeSpent;
    stepKeys: string[];
    stepCardProps: StepCardProps;
    summaryProps: SummaryProps;
    onClose: () => void;
}

const ESCAPE_BLOCKERS = '.modal.show, .modal.in, .kanban-sidebar';

const OccurrenceDrawer = ({
    occurrence,
    timeSpent,
    stepKeys,
    stepCardProps,
    summaryProps,
    onClose,
}: IOccurrenceDrawerProps) => {
    const { t, msg, formatDuration, formatNumber } = useMtText();
    const [showSummary, setShowSummary] = useState(false);
    const typeLabelId = getOccurrenceTypeLabelId(occurrence.occurrenceType);
    const city = useDealershipCityByDn(occurrence.dealershipDn, '');
    const isNextStamp = isNextStampOccurrence(occurrence);
    const finalized = !!occurrence.finished;
    const totalMs = getOccurrenceTotalMs(occurrence, timeSpent);

    const details: [string, string][] = [
        [msg('intl-msg:kanban.filterRow.customer'), occurrence.customer || '–'],
        [
            t('drawer.model'),
            [occurrence.vehicleModel || occurrence.model, occurrence.vehicleName].filter(Boolean).join(' · ') || '–',
        ],
        [
            msg('intl-msg:kanban.tableConfigs.odometer'),
            occurrence.vehicleOdometer !== undefined ? `${formatNumber(occurrence.vehicleOdometer)} km` : '–',
        ],
        [
            t('table.dealership'),
            [
                [occurrence.dealershipName, occurrence.dealershipDn ? `(DN ${occurrence.dealershipDn})` : '']
                    .filter(Boolean)
                    .join(' '),
                city === '-' ? '' : city,
            ]
                .filter(Boolean)
                .join(' · ') || '–',
        ],
        [
            msg('intl-msg:kanban.filterRow.consultant'),
            [
                timeSpent?.cpvName || occurrence.dealershipRepresentative,
                occurrence.dealershipRegional
                    ? `${msg('intl-msg:kanban.filterRow.regional')} ${occurrence.dealershipRegional}`
                    : '',
                occurrence.dealershipCell
                    ? `${msg('intl-msg:kanban.filterRow.cell')} ${occurrence.dealershipCell}`
                    : '',
            ]
                .filter(Boolean)
                .join(' · ') || '–',
        ],
        [msg('intl-msg:kanban.filterRow.type'), typeLabelId ? msg(typeLabelId) : '–'],
        [t('summary.totalTime'), formatDuration(totalMs)],
    ];

    return (
        <UptimeDrawer
            className='maintenance-tracking'
            width={760}
            escapeBlockers={ESCAPE_BLOCKERS}
            title={
                <span className='mt-drawer__title'>
                    {occurrence.vehicleLicensePlate || occurrence.vehicleChassis}
                    <CriticalityBadge value={occurrence.criticality} />
                </span>
            }
            subtitle={
                <span className='mt-mono'>
                    {[occurrence.vehicleChassis, occurrence.protocolNumber].filter(Boolean).join(' · ')}
                </span>
            }
            onClose={onClose}
        >
            <div className='mt-drawer'>
                <div className='mt-drawer__actions'>
                    <button type='button' className='mt-btn' onClick={() => setShowSummary(true)}>
                        <span className='rioglyph rioglyph-document' />
                        {t('drawer.summary')}
                    </button>
                </div>

                <dl className='mt-dmeta'>
                    {details.map(([label, value], index) => (
                        <div
                            key={label}
                            className={`mt-dmeta__row${index === details.length - 1 ? ' mt-dmeta__row--strong' : ''}`}
                        >
                            <dt>{label}</dt>
                            <dd>{value}</dd>
                        </div>
                    ))}
                </dl>

                <section className='mt-tl' aria-label={t('drawer.steps')}>
                    {stepKeys.map(stepKey => {
                        const step = toStepValue(stepKey);
                        const name = msg(getStepLabelId(step));
                        return (
                            <GenericCard
                                key={stepKey}
                                {...stepCardProps}
                                occurrence={occurrence}
                                summaryStep={findStep(occurrence, step)}
                                step={step}
                                finalized={finalized}
                                isNextStamp={isNextStamp}
                                renderCard={card => (
                                    <DrawerStepRow
                                        card={card}
                                        name={name}
                                        estimateMs={getStepEstimateMs(stepCardProps.steps ?? [], step)}
                                    />
                                )}
                            />
                        );
                    })}
                </section>
            </div>

            <OccurrenceSummaryDialog
                occurrence={occurrence}
                summaryProps={summaryProps}
                show={showSummary}
                onClose={() => setShowSummary(false)}
            />
        </UptimeDrawer>
    );
};

export default OccurrenceDrawer;
